import { blue } from "@ant-design/colors"
import { DownCircleTwoTone, PlusCircleOutlined, RightCircleFilled, SyncOutlined } from "@ant-design/icons"
import { Button, Collapse, message, Space } from "antd"
import classNames from "classnames"
import moment from "moment"
import React, { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { Attendee, Booker, BookerAttendeeInput, GetCourseDetailsByIdPublicQuery, GetUpdatedBookerDocument, NewBookingInput, useAddBookerAsAttendeeMutation } from "../../../../generated/graphql"
import { handleGenderText } from "../../../../helpers/handleGenderText"
import { useMediaSize } from "../../../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { URL_RESULTS } from "../../../../models/url"
import { margin } from "../../../../styles/layout"
import CreateEditAttendee from "../../../../views/CreateEditAttendee/CreateEditAttendee"
import PanelCard from "../../../../views/PanelCard/PanelCard"
import * as Sentry from "@sentry/react"
import { getGraphQLErrorMessage } from "../../../../helpers/sentry"
import SelectAttendeeTable from "../../../../views/SelectAttendeeTable/SelectAttendeeTable"
import SummarySection from "../../../../views/SummarySection/SummarySection"
import { NewBookingInputKeys } from "../../BookingSummary/BookingSummary"
import css from "../StepSummary2.module.css"
import AttendeeAlert from "./AttendeeAlert"
import classnames from "classnames"
import { attendeeCheck, effectiveBookingRestriction, isAttendeeSelectableForCourse } from "../../../../views/SelectAttendeeTable/checkAttendee"
import SummaryLeftSelfBooker from "./SummaryLeftSelfBooker"

const { Panel } = Collapse

type SummaryLeftProps = {
    booker: Booker
    loadingBooker: boolean
    attendee?: Attendee
    bookingData?: NewBookingInput
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    refetchBooker: () => void
    setAttendeeAgeDoesFitCourse: (arg0: boolean) => void
    attendeeAgeDoesFitCourse: boolean
    showAlert?: boolean
    isMemberOnlyCourse?: boolean
    isCourseWithPreRequisites?: boolean
    course?: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]
    setNoAttendeeDoesFitCourse: (arg0: boolean) => void
    setAllAttendeesAlreadyBooked: (arg0: boolean) => void
    selfBookerCourse?: boolean
    /**
     * Selbstbucher-Kurs, bei dem der eigene Teilnehmer-Datensatz des Buchers fehlt
     * ODER für diesen Kurs nicht buchbar ist, während andere Teilnehmer existieren.
     * Dann greift statt der automatischen Selbstbucher-Verdrahtung die normale
     * Teilnehmer-Auswahl (Bedingung siehe BookingSummary).
     */
    selfBookerAttendeeChoiceRequired?: boolean
    selectedAttendeeId: string | undefined
    setSelectedAttendeeId: (id: string) => void
}

type SelectedAttendeeProps = { attendee: BookerAttendeeInput }

export const SelectedAttendee: FC<SelectedAttendeeProps> = ({ attendee }) => {
    return (
        <>
            <div className={css.extraBig}>
                <span className={css.primary}>{attendee.firstname}</span>
                <span className={css.secondary}>{attendee.lastname}</span>
            </div>
            <div className={css.subTitle}>Geburtsdatum {moment(attendee.birthday).format("DD.MM.YY")}</div>
        </>
    )
}

const SummaryLeft: FC<SummaryLeftProps> = ({
    booker,
    loadingBooker,
    attendee,
    bookingData,
    updateBookingData,
    refetchBooker,
    setAttendeeAgeDoesFitCourse,
    attendeeAgeDoesFitCourse,
    showAlert,
    isMemberOnlyCourse,
    isCourseWithPreRequisites,
    course,
    setNoAttendeeDoesFitCourse,
    setAllAttendeesAlreadyBooked,
    selfBookerCourse,
    selfBookerAttendeeChoiceRequired,
    selectedAttendeeId,
    setSelectedAttendeeId,
}) => {
    const history = useHistory()
    const { t } = useTranslation()
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const [createEditAttendee, setCreateEditAttendee] = useState(false)
    const [editAttendeeMode, setEditAttendeeMode] = useState(false)
    const [attendeeToEdit, setAttendeeToEdit] = useState<BookerAttendeeInput>()
    const labelStyle = { gridColumn: "span 1" }
    const entryStyle = {
        gridColumn: "span 2",
        margin: "0 5px 0 0",
    }

    const bookerHasAttendees = Boolean(booker.attendees?.length && booker.attendees.length > 0)
    // Selbstbucher-Kurs, der ausnahmsweise die normale Teilnehmer-Auswahl braucht.
    const useAttendeeSelection = !selfBookerCourse || Boolean(selfBookerAttendeeChoiceRequired)
    const attendeeIsSelected = Boolean(selectedAttendeeId !== undefined)
    const courseId = bookingData?.courseId ?? undefined

    const bookingRestriction = useMemo(() => effectiveBookingRestriction(course), [course])

    // HANDLE THE CASE WHEN NO ATTENDEE IS SELECTED
    useEffect(() => {
        if (useAttendeeSelection && course) {
            if (booker.attendees?.length && booker.attendees.length > 0) {
                const checkedAttendees = booker.attendees.map((attendee) => {
                    return attendeeCheck(attendee, courseId, isCourseWithPreRequisites, bookingRestriction)
                })

                // Get list of allowed attendee IDs
                const allowedAttendeeIds = booker.attendees
                    .filter((attendee) =>
                        isAttendeeSelectableForCourse({
                            attendee,
                            courseId,
                            isCourseWithPreRequisites,
                            bookingRestriction,
                            isMemberOnlyCourse,
                        })
                    )
                    .map((attendee) => attendee.id)

                const firstAllowedAttendeeId = allowedAttendeeIds[0]

                const selectedAttendeeIsAllowed = allowedAttendeeIds.includes(selectedAttendeeId ?? "")

                if (!selectedAttendeeIsAllowed && firstAllowedAttendeeId) {
                    setSelectedAttendeeId(firstAllowedAttendeeId)
                    if (bookingData?.attendeeId !== firstAllowedAttendeeId) {
                        updateBookingData("attendeeId", firstAllowedAttendeeId)
                    }
                }

                const noAttendeeIsAllowed = firstAllowedAttendeeId === undefined

                setNoAttendeeDoesFitCourse(noAttendeeIsAllowed)

                // Sonderfall: kein Teilnehmer wählbar, WEIL alle bereits gebucht sind
                // (im Gegensatz zu Alters-/Mitglieds-/Voraussetzungs-Gründen). Erlaubt der
                // Fehlerliste, einen erklärenden Text mit klarem nächsten Schritt zu zeigen.
                setAllAttendeesAlreadyBooked(checkedAttendees.every((attendee) => attendee.allreadyBooked))
            }
        }
    }, [
        booker.attendees,
        bookingRestriction,
        courseId,
        isCourseWithPreRequisites,
        isMemberOnlyCourse,
        useAttendeeSelection,
        course,
        selectedAttendeeId,
        setNoAttendeeDoesFitCourse,
        setAllAttendeesAlreadyBooked,
        bookingData?.attendeeId,
        updateBookingData,
    ])

    const handleEditAttendee = (attendee: BookerAttendeeInput) => {
        // console.log("editAttendee:", attendee)
        setAttendeeToEdit(attendee)
        setEditAttendeeMode(true)
        setCreateEditAttendee(true)
    }

    const handleOk = (data: Pick<BookerAttendeeInput, "id" | "firstname">) => {
        // console.log("handleOk")
        // console.log("data: ", data)
        setEditAttendeeMode(false)
        setAttendeeToEdit(undefined)
        setCreateEditAttendee(false)
        if (bookingData?.attendeeId !== data.id) {
            // Check if the attendeeId has changed
            setSelectedAttendeeId(data.id)
            updateBookingData("attendeeId", data.id)
        }
        message.success(`Teilnehmer ${data.firstname} wurde gespeichert`)
    }

    const handleCancel = () => {
        setEditAttendeeMode(false)
        setAttendeeToEdit(undefined)
        setCreateEditAttendee(false)
        message.success("Änderungen nicht gespeichert")
    }

    // Dubletten-Umleitung (sc-3066): Wie handleOk, aber ohne "wurde gespeichert"-Toast —
    // es wurde nichts angelegt, der Bestands-Teilnehmer wird nur ausgewählt.
    const handleSelectExisting = (data: Pick<BookerAttendeeInput, "id" | "firstname">) => {
        setEditAttendeeMode(false)
        setAttendeeToEdit(undefined)
        setCreateEditAttendee(false)
        if (bookingData?.attendeeId !== data.id) {
            setSelectedAttendeeId(data.id)
            updateBookingData("attendeeId", data.id)
        }
        message.success(t("createEditAttendee.duplicate.usingExisting", { firstname: data.firstname }))
    }

    const Booker = () => {
        return (
            <>
                <PanelCard
                    title={"Anschrift"}
                    columns={1}
                    style={{
                        fontWeight: 500,
                        borderColor: "lightgrey",
                    }}
                    smallTitle
                    gapAfter
                >
                    <div>{booker.street + " " + booker.houseNumber}</div>
                    <div>{booker.postCode + " " + booker.city}</div>
                </PanelCard>
                <PanelCard
                    title={"Kontaktdaten"}
                    columns={3}
                    style={{
                        fontWeight: 500,
                        borderColor: "lightgrey",
                    }}
                    smallTitle
                >
                    <div style={labelStyle}>{"Telefon"}</div>
                    <div style={{ overflowWrap: "break-word", ...entryStyle }}>{booker.phoneNumber}</div>
                    <div style={labelStyle}>{"Mobil"}</div>
                    <div style={{ overflowWrap: "break-word", ...entryStyle }}>{booker.mobileNumber}</div>
                    <div style={labelStyle}>{"Email"}</div>
                    <div style={{ overflowWrap: "break-word", ...entryStyle }}>{booker.email}</div>
                </PanelCard>
            </>
        )
    }

    const [addBookerAsAttendee, { loading: addingAttendee }] = useAddBookerAsAttendeeMutation()
    const setBookerAsAttendee = async () => {
        try {
            const res = await addBookerAsAttendee({
                variables: { bookerId: booker.id },
                refetchQueries: [{ query: GetUpdatedBookerDocument, variables: { skip: !booker?.id, id: booker?.id ?? "" } }],
            })
            if (res.data?.addBookerAsAttendee?.id) {
                const newAttendeeData = res.data.addBookerAsAttendee
                const createdAttendee = newAttendeeData.attendees?.find((a) => a.customerId === newAttendeeData.customerId)
                if (createdAttendee) {
                    handleOk({ id: createdAttendee.id, firstname: createdAttendee.customer.firstname })
                }
            }
        } catch (error: any) {
            handleCancel()
            console.log("setBookerAsAttendee error: ", error)
            const rawErrorMessage = getGraphQLErrorMessage(error) || ""
            if (rawErrorMessage.includes("Access token not valid")) {
                history.push(URL_RESULTS.replace(":resultType", "loggedOut"))
            } else {
                Sentry.captureException(error)
                history.push(URL_RESULTS.replace(":resultType", "unknown"))
            }
        }
    }

    // Trägt der Bucher bereits einen eigenen Teilnehmer-Datensatz? Der Vergleich läuft über
    // die Customer-Identität: Attendee-Id und Booker-Id sind verschiedene Domänen und stimmen
    // nie überein — der Button wurde dadurch auch dann angeboten, wenn der Datensatz längst
    // existierte, und das Backend lehnte die Anlage anschließend ab.
    const bookerAlreadyAttendee = Boolean(booker.customer?.id && booker.attendees?.some((attendee) => attendee.customer?.id === booker.customer?.id))

    const attendeesFirstName = booker.attendees
        ?.filter((attendee) => {
            return attendee.id === bookingData?.attendeeId
        })
        .map((attendee) => `${attendee.firstname}`)[0]

    const attendeesLastName = booker.attendees
        ?.filter((attendee) => {
            return attendee.id === bookingData?.attendeeId
        })
        .map((attendee) => `${attendee.lastname}`)[0]

    const renderAttendee = () => {
        return (
            <div style={{ paddingBottom: margin.s }}>
                <>
                    {createEditAttendee ? (
                        <>
                            {attendeeToEdit ? (
                                <SummarySection
                                    title={editAttendeeMode ? "Teilnehmer bearbeiten" : "Teilnehmer anlegen"}
                                    style={{ marginBottom: 0 }}
                                    content={<SelectedAttendee attendee={attendeeToEdit} />}
                                />
                            ) : (
                                <SummarySection title={"Teilnehmer anlegen"} style={{ marginBottom: 0 }} />
                            )}
                            <SummarySection
                                style={{ marginBottom: 0 }}
                                content={
                                    <PanelCard
                                        columns={1}
                                        style={{
                                            fontWeight: 500,
                                            borderColor: "lightgrey",
                                            display: "flex",
                                            flexDirection: "column",
                                            marginTop: 0,
                                            paddingTop: 0,
                                        }}
                                        smallTitle
                                        gapAfter
                                    >
                                        <CreateEditAttendee
                                            editMode={editAttendeeMode}
                                            attendeeToEdit={attendeeToEdit}
                                            onOk={handleOk}
                                            onSelectExisting={handleSelectExisting}
                                            onCancel={handleCancel}
                                            booker={booker}
                                            courseId={bookingData?.courseId ?? undefined}
                                            showAlert={showAlert}
                                        />
                                    </PanelCard>
                                }
                            />
                        </>
                    ) : (
                        <>
                            <PanelCard
                                data-testid='select-attendee-panel'
                                title={
                                    bookerHasAttendees ? (
                                        <Space style={{ justifyContent: "space-between" }}>
                                            <span>Teilnehmer wählen</span>
                                            <Button
                                                type='text'
                                                // onClick={() => updateBookerData(booker)}
                                                onClick={() => refetchBooker()}
                                                icon={<SyncOutlined style={{ color: blue[5] }} />}
                                            ></Button>
                                        </Space>
                                    ) : null
                                }
                                columns={1}
                                style={{
                                    fontWeight: 500,
                                    borderColor: "lightgrey",
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: bookerHasAttendees ? undefined : 0,
                                    margin: bookerHasAttendees ? undefined : 0,
                                }}
                                smallTitle
                                gapAfter
                            >
                                {bookerHasAttendees ? (
                                    <SelectAttendeeTable
                                        data-testid='select-attendee-table-container'
                                        booker={booker}
                                        loadingBooker={loadingBooker}
                                        refetchBooker={refetchBooker}
                                        attendees={booker.attendees}
                                        selectedAttendeeId={selectedAttendeeId}
                                        setSelectedAttendee={(attendeeId) => setSelectedAttendeeId(attendeeId)}
                                        updateBookingData={(key, value) => updateBookingData(key, value)}
                                        editAttendee={(attendee) => handleEditAttendee(attendee)}
                                        courseId={bookingData?.courseId ?? undefined}
                                        setAttendeeAgeDoesFitCourse={setAttendeeAgeDoesFitCourse}
                                        attendeeAgeDoesFitCourse={attendeeAgeDoesFitCourse}
                                        isMemberOnlyCourse={isMemberOnlyCourse}
                                        isCourseWithPreRequisites={isCourseWithPreRequisites}
                                        bookingRestriction={bookingRestriction}
                                    />
                                ) : null}
                                <Button type='primary' ghost block icon={<PlusCircleOutlined />} onClick={() => setCreateEditAttendee(true)} style={{ marginTop: bookerHasAttendees ? margin.s : 0 }}>
                                    Teilnehmer neu anlegen
                                </Button>
                                {bookerAlreadyAttendee ? null : (
                                    <Button type='primary' ghost block icon={<PlusCircleOutlined />} loading={addingAttendee} onClick={() => setBookerAsAttendee()} style={{ marginTop: margin.s }}>
                                        Bucher als Teilnehmer verwenden
                                    </Button>
                                )}
                            </PanelCard>
                        </>
                    )}
                </>
            </div>
        )
    }

    return (
        <>
            <Collapse
                ghost
                expandIconPosition='end'
                defaultActiveKey={media === "xs" ? ["attendee"] : ["attendee", "booker"]}
                activeKey={useAttendeeSelection ? ["attendee", "booker"] : ["booker"]}
                className={css.collapse}
                expandIcon={(panelProps) => {
                    return (
                        <>
                            {useAttendeeSelection && (
                                <div className={css.collapseIcon}>
                                    {panelProps.isActive ? (
                                        <DownCircleTwoTone className={css.collapseIcon} style={{ marginTop: 20 }} />
                                    ) : (
                                        <RightCircleFilled style={{ marginTop: 20, color: blue[5] }} className={css.collapseIcon} />
                                    )}
                                </div>
                            )}
                        </>
                    )
                }}
            >
                {!useAttendeeSelection ? (
                    <>
                        <Panel
                            className={css.collapsePanel}
                            key='booker'
                            header={
                                <>
                                    <div className={classNames(css.header, css.underLine)}>{handleGenderText({ type: "booker", gender: booker.gender ?? undefined })}</div>
                                    <div className={css.extraBig} style={{ overflowWrap: "break-word", maxWidth: "100%" }}>
                                        <span className={css.primary}>{booker.firstname}</span>
                                        <span className={css.secondary}>{booker.lastname}</span>
                                    </div>
                                </>
                            }
                        >
                            <div className={css.innerPanelContainer}>
                                <SummaryLeftSelfBooker
                                    booker={booker}
                                    updateBookingData={updateBookingData}
                                    setBookerAsAttendee={setBookerAsAttendee}
                                    refetchBooker={refetchBooker}
                                    bookingData={bookingData}
                                    setAttendeeAgeDoesFitCourse={setAttendeeAgeDoesFitCourse}
                                    isCourseWithPreRequisites={isCourseWithPreRequisites}
                                    bookingRestriction={bookingRestriction}
                                    isMemberOnlyCourse={isMemberOnlyCourse}
                                />
                            </div>
                        </Panel>
                    </>
                ) : (
                    <>
                        <Panel
                            data-testid='attendee-panel'
                            className={!bookerHasAttendees || !attendeeIsSelected ? classnames(css.collapsePanel, css.alertPanel) : css.collapsePanel}
                            collapsible='disabled'
                            showArrow={false}
                            // showArrow={bookerHasAttendees && attendeeIsSelected ? true : false}
                            key='attendee'
                            header={
                                !bookerHasAttendees || !attendeeIsSelected ? (
                                    <AttendeeAlert selectedAttendeeId={selectedAttendeeId} attendees={booker.attendees} selfBookerCourse={selfBookerCourse} />
                                ) : (
                                    <>
                                        <div className={classNames(css.header, css.underLine)}>{handleGenderText({ type: "attendee", gender: attendee?.gender })}</div>
                                        <div className={css.extraBig}>
                                            <span className={css.primary}>{attendeesFirstName}</span>
                                            <span className={css.secondary}>{attendeesLastName}</span>
                                        </div>
                                        <div className={css.subTitle}>Geburtsdatum {moment(attendee?.birthday).format("DD.MM.YY")}</div>
                                    </>
                                )
                            }
                        >
                            <div className={css.innerPanelContainer}>
                                <SummarySection
                                    // title={handleGenderText({ type: "attendee", gender: attendee?.gender })}
                                    style={{ marginBottom: 0, padding: 0, paddingBottom: 0 }}
                                    content={renderAttendee()}
                                />
                            </div>
                        </Panel>
                        <Panel
                            data-testid='booker-panel'
                            className={css.collapsePanel}
                            key='booker'
                            showArrow={false}
                            // extra={<Button type='link' icon={<InfoCircleTwoTone />} />}
                            header={
                                <>
                                    <div className={classNames(css.header, css.underLine)}>{handleGenderText({ type: "booker", gender: booker.gender ?? undefined })}</div>
                                    <div className={css.extraBig} style={{ overflowWrap: "break-word", maxWidth: "100%" }}>
                                        <span className={css.primary}>{booker.firstname}</span>
                                        <span className={css.secondary}>{booker.lastname}</span>
                                    </div>
                                </>
                            }
                        >
                            <div className={css.innerPanelContainer}>
                                <SummarySection /* title={handleGenderText({ type: "booker", gender: booker.gender ?? undefined })} */ style={{ marginBottom: 0 }} content={<Booker />} />
                            </div>
                        </Panel>
                    </>
                )}
            </Collapse>
        </>
    )
}

export default SummaryLeft
